import React, { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/ui/modal';
import { fileService } from '../../../services/fileService';

interface ImageViewerModalProps {
    isOpen: boolean;
    onClose: () => void;
    images: string[];
    title?: string;
}

type ImageStatus = 'pending' | 'ready' | 'error';

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
    isOpen,
    onClose,
    images,
    title = 'Image Viewer'
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    // Always render *signed* URLs so private-bucket canonical URLs don't hit
    // ERR_BLOCKED_BY_ORB (Chrome blocks S3's 403 XML response).
    const [resolvedImages, setResolvedImages] = useState<string[]>([]);
    const [statuses, setStatuses] = useState<ImageStatus[]>([]);

    // Stable signature so identical-content arrays don't retrigger resolution.
    const imagesKey = images.join('|');

    useEffect(() => {
        if (isOpen) setCurrentIndex(0);
    }, [isOpen, imagesKey]);

    useEffect(() => {
        let cancelled = false;
        if (!isOpen || images.length === 0) {
            setResolvedImages([]);
            setStatuses([]);
            return;
        }
        setResolvedImages(new Array(images.length).fill(''));
        setStatuses(new Array(images.length).fill('pending') as ImageStatus[]);
        images.forEach(async (raw, idx) => {
            try {
                const url = await fileService.resolveMediaUrl(raw);
                if (cancelled) return;
                setResolvedImages((prev) => {
                    const next = [...prev];
                    next[idx] = url || '';
                    return next;
                });
                setStatuses((prev) => {
                    const next = [...prev];
                    next[idx] = url ? 'ready' : 'error';
                    return next;
                });
            } catch (err) {
                console.error('ImageViewerModal: resolve failed', { raw, err });
                if (cancelled) return;
                setStatuses((prev) => {
                    const next = [...prev];
                    next[idx] = 'error';
                    return next;
                });
            }
        });
        return () => {
            cancelled = true;
        };
    }, [isOpen, imagesKey]);

    if (!isOpen || images.length === 0) return null;

    const currentSrc = resolvedImages[currentIndex] ?? '';
    const currentStatus: ImageStatus = statuses[currentIndex] ?? 'pending';

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const markCurrentError = () => {
        setStatuses((prev) => {
            if (prev[currentIndex] === 'error') return prev;
            const next = [...prev];
            next[currentIndex] = 'error';
            return next;
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={true}
            className="max-w-4xl bg-black/90 p-0 text-white"
        >
            <div className="relative flex min-h-[500px] flex-col items-center justify-center p-4">
                {/* Header */}
                <div className="absolute left-0 top-0 z-10 w-full bg-black/50 p-4 backdrop-blur-sm">
                    <h3 className="text-lg font-semibold text-white">{title}</h3>
                    <p className="text-sm text-gray-300">
                        {currentIndex + 1} of {images.length}
                    </p>
                </div>

                {/* Main Image */}
                <div className="flex h-full w-full items-center justify-center overflow-hidden">
                    {currentStatus === 'error' ? (
                        <div className="flex flex-col items-center gap-2 px-6 text-center text-gray-300">
                            <svg className="h-12 w-12 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" />
                            </svg>
                            <span className="text-sm font-semibold">Could not load this image</span>
                            <span className="text-xs text-gray-400">The link may have expired or the file is unavailable.</span>
                        </div>
                    ) : currentStatus === 'ready' && currentSrc ? (
                        <img
                            src={currentSrc}
                            alt={`View ${currentIndex + 1}`}
                            className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
                            onError={markCurrentError}
                        />
                    ) : (
                        <div className="flex flex-col items-center gap-2 text-gray-300">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/30 border-t-white" />
                            <span className="text-xs">Loading image…</span>
                        </div>
                    )}
                </div>

                {/* Navigation Buttons */}
                {images.length > 1 && (
                    <>
                        <button
                            onClick={handlePrev}
                            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-all hover:bg-white/20 backdrop-blur-md"
                        >
                            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button
                            onClick={handleNext}
                            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-all hover:bg-white/20 backdrop-blur-md"
                        >
                            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </>
                )}

                {/* Thumbnails (optional, if needed later) */}
                {images.length > 1 && (
                    <div className="absolute bottom-4 flex gap-2 overflow-x-auto rounded-xl bg-black/50 p-2 backdrop-blur-sm">
                        {images.map((_img, idx) => {
                            const thumbSrc = resolvedImages[idx] ?? '';
                            return (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentIndex(idx)}
                                    className={`relative h-12 w-12 overflow-hidden rounded-lg transition-all ${currentIndex === idx ? 'ring-2 ring-blue-500' : 'opacity-50 hover:opacity-100'
                                        }`}
                                >
                                    {thumbSrc ? (
                                        <img src={thumbSrc} alt={`Thumb ${idx}`} className="h-full w-full object-cover" />
                                    ) : (
                                        <span className="block h-full w-full bg-white/10" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ImageViewerModal;
